/**
 * Recovery Key utility functions for Matrix backup recovery
 * Uses the Matrix SDK's implementation for MSC1703 compliance
 */

import logger from '../../utils/logger';

/**
 * @typedef {Object} RecoveryKeyValidationResult
 * @property {string} normalized - Normalized recovery key string
 * @property {boolean} isFormatValid - Whether the format is valid
 * @property {string} [reason] - Reason for invalidity if format is invalid
 */

/**
 * Validates a Matrix recovery key string using the SDK's validation.
 * Recovery keys must be in the standard Matrix format:
 * - 12 groups of 4 Base58 characters (total 48 chars)
 * - Includes MSC1703 validation (prefix and parity checks)
 *
 * @param input - The recovery key input to validate
 * @returns Validation result with normalized string and validity status
 *
 * @example
 * // Standard Matrix format (groups of 4)
 * validateRecoveryKey('EsTz DCbY xsLA nLNt bEso vyU7 NygK N9GN P1jG eS88 7qz7 ohvw')
 */
export async function validateRecoveryKey(input) {
  // console.log('RECOVERY [validateRecoveryKey] Input:', input); // Removed for security
  logger.debug('RECOVERY [validateRecoveryKey] Input length:', input?.length);

  // Check for empty input
  if (!input || input.trim().length === 0) {
    logger.debug('RECOVERY [validateRecoveryKey] Empty input');
    return {
      normalized: '',
      isFormatValid: false,
      reason: 'EMPTY',
    };
  }

  // Normalize to standard format: remove extra whitespace
  const normalized = input.trim().replace(/\s+/g, ' ');
  // console.log('RECOVERY [validateRecoveryKey] Normalized:', normalized); // Removed for security

  try {
    // Try to decode using the SDK's function
    // This will validate MSC1703 compliance (prefix and parity)
    const cryptoApi = await import('matrix-js-sdk/lib/crypto-api');
    logger.debug('RECOVERY [validateRecoveryKey] SDK imported, has decodeRecoveryKey:', !!cryptoApi.decodeRecoveryKey);

    if (!cryptoApi.decodeRecoveryKey) {
      logger.debug('RECOVERY [validateRecoveryKey] SDK unavailable');
      return {
        normalized,
        isFormatValid: false,
        reason: 'SDK_UNAVAILABLE',
      };
    }

    // The SDK's decodeRecoveryKey will throw if invalid
    // console.log('RECOVERY [validateRecoveryKey] Attempting SDK decode with:', normalized); // Removed for security
    await cryptoApi.decodeRecoveryKey(normalized);
    logger.debug('RECOVERY [validateRecoveryKey] SDK decode succeeded');

    // If we get here, the key is valid
    // Format it as groups of 4 for consistency
    const rawKey = normalized.replace(/\s/g, '');
    const formattedKey = rawKey.match(/.{1,4}/g)?.join(' ') || normalized;
    // console.log('RECOVERY [validateRecoveryKey] Final formatted:', formattedKey); // Removed for security

    return {
      normalized: formattedKey,
      isFormatValid: true,
    };
  } catch (error) {
    logger.debug('RECOVERY [validateRecoveryKey] SDK decode failed:', error.message);
    logger.debug('RECOVERY [validateRecoveryKey] Error stack:', error.stack);

    // Determine the specific validation failure
    let reason = 'INVALID_FORMAT';

    if (error.message?.includes('parity')) {
      reason = 'INVALID_PARITY';
    } else if (error.message?.includes('prefix')) {
      reason = 'INVALID_PREFIX';
    } else if (error.message?.includes('length')) {
      reason = 'INVALID_LENGTH';
    } else if (error.message?.includes('alphabet') || error.message?.includes('Base58')) {
      reason = 'INVALID_ALPHABET';
    } else if (error.message?.includes('Incorrect length')) {
      reason = 'INVALID_LENGTH';
    }

    logger.debug('RECOVERY [validateRecoveryKey] Determined reason:', reason);

    return {
      normalized,
      isFormatValid: false,
      reason,
    };
  }
}

/**
 * @typedef {Uint8Array} RecoveryKeyBytes
 */

/**
 * Decodes a recovery key string into raw bytes.
 * Uses the Matrix SDK's decodeRecoveryKey function for MSC1703 compliance.
 *
 * @param keyString - The recovery key string (any format)
 * @returns Promise resolving to the decoded bytes
 * @throws Error if the key format is invalid or SDK is unavailable
 */
export async function decodeRecoveryKeyString(keyString) {
  // console.log('RECOVERY [decodeRecoveryKeyString] Input:', keyString); // Removed for security

  // First validate the key
  const validation = await validateRecoveryKey(keyString);
  logger.debug('RECOVERY [decodeRecoveryKeyString] Validation result:', validation);

  if (!validation.isFormatValid) {
    const error = new Error(`Invalid recovery key: ${validation.reason}`);
    error.code = validation.reason || 'INVALID_FORMAT';
    logger.debug('RECOVERY [decodeRecoveryKeyString] Validation failed, throwing:', error.message);
    throw error;
  }

  try {
    // Import the SDK's crypto API
    const cryptoApi = await import('matrix-js-sdk/lib/crypto-api');

    if (!cryptoApi.decodeRecoveryKey) {
      const error = new Error('Matrix SDK decodeRecoveryKey not available');
      error.code = 'SDK_UNAVAILABLE';
      throw error;
    }

    // console.log('RECOVERY [decodeRecoveryKeyString] Decoding with SDK, input:', validation.normalized); // Removed for security
    // Use the SDK's decodeRecoveryKey which handles MSC1703
    // This returns the raw key bytes after validating prefix and parity
    const bytes = cryptoApi.decodeRecoveryKey(validation.normalized);
    logger.debug('RECOVERY [decodeRecoveryKeyString] SDK returned bytes:', bytes?.length);
    return new Uint8Array(bytes);
  } catch (err) {
    logger.debug('RECOVERY [decodeRecoveryKeyString] Decode error:', err.message);
    logger.debug('RECOVERY [decodeRecoveryKeyString] Error stack:', err.stack);

    // Re-throw SDK errors with proper context
    if (err.code === 'SDK_UNAVAILABLE') {
      throw err;
    }

    // Wrap other errors with more context
    const error = new Error(`Failed to decode recovery key: ${err.message}`);
    error.code = err.errcode || 'DECODE_ERROR';
    error.originalError = err;
    throw error;
  }
}
